import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import WidgetShell from '../WidgetShell';
import Badge from '@/components/ui/Badge';
import { useRecentOrders, type RecentOrder } from '@/hooks/dashboard/useEcomDashboard';
import { formatCurrency, formatDate } from '@/lib/utils';

const statusVariant: Record<RecentOrder['status'], 'yellow' | 'blue' | 'green' | 'red'> = {
  pending: 'yellow',
  processing: 'blue',
  shipped: 'blue',
  delivered: 'green',
  cancelled: 'red',
};

export default function RecentOrdersWidget() {
  const { data: orders, isLoading, error } = useRecentOrders();

  return (
    <WidgetShell
      title="Recent Orders"
      icon={<ShoppingCart />}
      to="/admin/orders"
      isLoading={isLoading}
      error={error}
      isEmpty={!!orders && orders.length === 0}
      emptyMessage="No recent orders"
      emptyIcon={<ShoppingCart />}
      skeletonClassName="h-40"
    >
      {orders && orders.length > 0 && (
        <ul className="divide-y divide-line">
          {orders.slice(0, 6).map((order) => (
            <li key={order.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {order.order_number}
                </p>
                <p className="truncate text-xs text-stone-500">
                  {order.customer_name} · {formatDate(order.created_at)}
                </p>
              </div>
              <Badge variant={statusVariant[order.status] ?? 'gray'}>{order.status}</Badge>
              <span className="w-20 text-right text-sm font-medium text-ink">
                {formatCurrency(order.total)}
              </span>
              <Link
                to={`/admin/orders/${order.id}`}
                className="text-stone-400 hover:text-green-700"
                aria-label={`View order ${order.order_number}`}
              >
                <Eye className="h-4 w-4" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}
